import { Attribute, AttributeGroup, Category, Option, OptionsGetter, ProductFormValues, ProductInput } from "components/products/types";
import api from "api/api";

export const debounce = (func: (...args: any[]) => void, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function (this: void, ...args: any[]) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const fetchOptions = async (optionsGetter: OptionsGetter, query: string): Promise<Option[]> => {
  if (query.length < optionsGetter.minlength) {
    return [];
  }
  try {
    const response = await api.get(
      `${optionsGetter.url}?${optionsGetter.param.key}=${query}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching options:", error);
    return [];
  }
};

export const createCompositeKey = (variant: any, attributes: Attribute[]) => {
  return attributes
    .map((attr) => variant[attr.attribute_code]?.option_id || "")
    .join("-");
};

export const validateRequiredFields = (
  formValues: ProductFormValues,
  attributeGroups: AttributeGroup[]
): string[] => {
  const invalidFields: string[] = [];

  // Validate attribute groups
  attributeGroups.forEach((group) => {
    if (typeof group.attributes !== "string") {
      const excludedAttributes = ["category_ids", "sku"];
      group.attributes.forEach((attribute) => {
        if (
          attribute.is_required === "1" &&
          !formValues[attribute.attribute_code] &&
          !excludedAttributes.includes(attribute.attribute_code)
        ) {
          invalidFields.push(attribute.attribute_code);
        }
      });
    }
  });

  // Special validation: When special_price is set, special_from_date and special_to_date are required
  if (formValues["special_price"] && formValues["special_price"].toString().trim() !== '') {
    if (!formValues["special_from_date"]) {
      invalidFields.push("special_from_date");
    }
    if (!formValues["special_to_date"]) {
      invalidFields.push("special_to_date");
    }
  }

  return invalidFields;
};

export const validateRequiredFieldsForVariants = (
  formValues: ProductFormValues,
  attributeGroups: AttributeGroup[]
): string | null => {
  const invalidFields: string[] = [];
  
  // Required fields that must be filled before creating variants
  const requiredFields = ['name', 'price', 'short_description', 'description', 'weight'];
  
  // Check if category is selected
  if (!formValues.category?.level2) {
    invalidFields.push('category');
  }
  
  // Check required fields from common attributes
  const missingFields = requiredFields.filter(field => {
    return !formValues[field] || formValues[field] === '';
  });
  
  if (missingFields.length > 0) {
    invalidFields.push(...missingFields);
  }
  
  // Check if gallery is required
  const galleryRequired = attributeGroups.some(group => {
    if (typeof group.attributes !== "string") {
      return group.attributes.some(attr => 
        attr.attribute_code === "gallery" && 
        attr.is_required === "1" && 
        (!formValues.gallery || formValues.gallery.length === 0)
      );
    }
    return false;
  });
  
  if (galleryRequired && (!formValues.gallery || formValues.gallery.length === 0)) {
    invalidFields.push('gallery');
  }
  
  // Check other required fields
  attributeGroups.forEach((group) => {
    if (typeof group.attributes !== "string") {
      const excludedAttributes = ["category_ids", "sku", ...requiredFields];
      group.attributes.forEach((attribute) => {
        if (
          attribute.is_required === "1" &&
          !formValues[attribute.attribute_code] &&
          !excludedAttributes.includes(attribute.attribute_code)
        ) {
          invalidFields.push(attribute.attribute_code);
        }
      });
    }
  });
  
  if (invalidFields.length > 0) {
    if (invalidFields.includes('category')) {
      return "Please select a category before creating variants";
    } else if (invalidFields.includes('gallery')) {
      return "Product images are required before creating variants";
    } else {
      return "Please fill all required fields before creating variants";
    }
  }
  
  return null; // No errors
};

// Maps for option values
export const livraisonMap: Record<string, number> = {
  "Oui": 17952,
  "Non": 17953
};

export const politiqueMap: Record<string, number> = {
  "Non éligible": 17956,
  "14 jours": 17955,
  "30 jours": 17954
};

export const reverseLivraisonMap = Object.entries(livraisonMap).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {} as Record<number, string>);

export const reversePolitiqueMap = Object.entries(politiqueMap).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {} as Record<number, string>);

export const getMappedValue = (
  map: Record<string, number>,
  reverseMap: Record<number, string>,
  value: any,
  defaultValue: number
) => {
  const selectedLabel = Array.isArray(value) && value.length > 0
    ? reverseMap[value[0]] || value[0]
    : reverseMap[value] || value;
  return map[selectedLabel] || defaultValue;
};

export const roleMapping: Record<string, string> = {
  Base: "image",
  Small: "small_image",
  Thumbnail: "thumbnail",
  OG_Image: "magefan_og_image",
  Swatch: "swatch_image",
};

export const formatProductPayload = (formValues: ProductFormValues, characteristicsAttributes: Attribute[], selectedAttributes: Attribute[], generatedVariants: any[]): ProductInput => {
  // Format the Gallery Correctly
  const formattedGallery = Array.isArray(formValues.gallery)
    ? formValues.gallery.map((image) => ({
      preview: image.originalUrl,
      roles: image.roles
        ? Object.keys(image.roles)
          .filter((role) => image.roles && image.roles[role] === true)
          .map((role) => roleMapping[role])
          .filter(Boolean)
        : [],
    }))
    : [];

  // Extract custom attributes from Caractéristiques
  const customAttributes = characteristicsAttributes
    .map((attr) => {
      const value = formValues[attr.attribute_code];
      if (!value || value === "") return null;

      return {
        code: attr.attribute_code,
        value: Array.isArray(value)
          ? value.map((v) => v.option_id || v).join(",")
          : value.option_id || value,
      };
    })
    .filter(Boolean);

  // Prepare the product input object
  const productInput: ProductInput = {
    name: formValues.name || "Untitled Product",
    category: {
      level2: formValues.category?.level2 ? formValues.category.level2.id : null,
      level3: formValues.category?.level3 ? formValues.category.level3.id : null,
      level4: formValues.category?.level4 ? formValues.category.level4.id : null,
    },
    brand: formValues.brand && formValues.brand[0]
      ? parseInt(formValues.brand[0].option_id, 10)
      : undefined,
    model_name: formValues.Nom_du_modele || "",
    model_number: formValues.Numero_du_modele || "",
    seller_reference: formValues.reference || "",
    short_description: formValues.short_description || "",
    description: formValues.description || "",
    gallery: formattedGallery,
    price: parseFloat(formValues.price as string) || 0,

    special_price: formValues.special_price || formValues.special_from_date || formValues.special_to_date
      ? {
        value: parseFloat(formValues.special_price as string) || 0,
        from_date: formValues.special_from_date
          ? new Date(formValues.special_from_date).toISOString().split("T")[0]
          : "",
        end_date: formValues.special_to_date
          ? new Date(formValues.special_to_date).toISOString().split("T")[0]
          : "",
      }
      : undefined,

    Livraison_gratuite: typeof formValues.Livraison_gratuite === 'object' && formValues.Livraison_gratuite 
      ? parseInt(String((formValues.Livraison_gratuite as Option).option_id || 0), 10) 
      : 0,
      
    politique_de_retour: typeof formValues.politique_de_retour === 'object' && formValues.politique_de_retour 
      ? parseInt(String((formValues.politique_de_retour as Option).option_id || 0), 10) 
      : 0,

    warranty: formValues.garantie && typeof formValues.garantie === 'object'
      ? parseInt(String((formValues.garantie as Option).option_id || 0), 10) 
      : 0,
      
    mode_expedition: formValues.mode_expedition
      ? typeof formValues.mode_expedition === 'object' 
        ? parseInt(String((formValues.mode_expedition as Option).option_id || 0), 10) || 0
        : parseInt(String(formValues.mode_expedition), 10) || 0
      : 0,
      
    quantity: typeof formValues.mode_expedition === 'object' && 
              (formValues.mode_expedition as Option).option_id === "16500"
      ? undefined
      : parseInt(formValues.quantity_and_stock_status as string, 10) || 0,

    weight: parseFloat(formValues.weight as string) || 0,
    dimensions_de_l_outil_largeur1: parseFloat(formValues.dimensions_de_l_outil_largeur1 as string) || 0,
    dimensions_de_l_outil_hauteur1: parseFloat(formValues.dimensions_de_l_outil_hauteur1 as string) || 0,
    longueur25: parseFloat(formValues.longueur25 as string) || 0,
    
    attributes: customAttributes.filter((attr): attr is { code: string; value: string | number } => attr !== null),

    selectedAttributes:
      selectedAttributes.length > 0
        ? selectedAttributes.map((attr) => attr.attribute_code)
        : undefined,

    variantsData:
      generatedVariants.length > 0
        ? generatedVariants.map((variant) => ({
          quantity: typeof formValues.mode_expedition === 'object' && 
                    (formValues.mode_expedition as Option).option_id === "16500"
            ? 0
            : parseInt(variant.quantity as string, 10) || 0,
          price: parseFloat(variant.price as string) || 0,

          attributes: selectedAttributes
            .map((attr) => ({
              code: attr.attribute_code,
              value: String(variant[attr.attribute_code]?.option_id || ""),
            }))
            .filter((attr) => attr.value !== ""),

          gallery: variant.image
            ? {
              preview: variant.image.originalUrl,
              roles: variant.image.roles
                ? Object.keys(variant.image.roles).filter((role) => variant.image.roles[role] === true)
                : [],
            }
            : undefined,
        }))
        : undefined,
  };

  // Remove `quantity` from product if `variantsData` and `selectedAttributes` exist
  if (productInput.variantsData?.length && productInput.selectedAttributes?.length) {
    delete productInput.quantity;
  }

  return productInput;
};
